import Axios, { AxiosError, CanceledError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { LOCALSTORAGE_TOKEN_KEY, useAuth } from "@/hooks/auth/auth";
import type { ErrorDetails } from "@/interfaces/error-details";
import { markApiError, sanitizeApiRequestPath, type ApiErrorRequestContext } from "@/lib/error-classification";
import { openBugReport, openSupportEmail } from "@/lib/support-actions";
import { useEffect } from "react";
import { toast } from "sonner";

const AXIOS_INSTANCE = Axios.create({ baseURL: "" });
const FRONTEND_BUILD_HEADER_NAME = "X-Veronics-Frontend-Build";
const BUILD_MISMATCH_HEADER_NAME = "X-Veronics-Build-Mismatch";
const FRONTEND_BUILD_ID = __VERONICS_FRONTEND_BUILD_ID__;
const BUILD_MISMATCH_TOAST_ID = "veronics-build-mismatch";

let handleUnauthorizedResponse: (() => void) | null = null;
let hasShownBuildMismatchToast = false;

const getHeader = (
  headers: AxiosRequestConfig["headers"] | AxiosResponse["headers"] | undefined,
  headerName: string,
) => {
  if (!headers) {
    return undefined;
  }

  if ("get" in headers && typeof headers.get === "function") {
    return headers.get(headerName);
  }

  const headerRecord = headers as Record<string, unknown>;
  return headerRecord[headerName] ?? headerRecord[headerName.toLowerCase()];
};

const hasAuthorizationHeader = (headers: AxiosRequestConfig["headers"]) => Boolean(getHeader(headers, "Authorization"));

const setRequestHeader = (headers: AxiosRequestConfig["headers"], headerName: string, value: string) => {
  if (!headers) {
    return;
  }

  if ("set" in headers && typeof headers.set === "function") {
    headers.set(headerName, value);
    return;
  }

  (headers as Record<string, unknown>)[headerName] = value;
};

const isBuildMismatchResponse = (response: AxiosResponse | undefined) =>
  String(getHeader(response?.headers, BUILD_MISMATCH_HEADER_NAME)).toLowerCase() === "true";

const showBuildMismatchToast = () => {
  if (hasShownBuildMismatchToast) {
    return;
  }

  hasShownBuildMismatchToast = true;

  toast.info("A new version of Veronics is available.", {
    id: BUILD_MISMATCH_TOAST_ID,
    description: "Finish the update to load the latest site changes.",
    action: {
      label: "Finish Update",
      onClick: () => {
        window.location.reload();
      },
    },
    closeButton: true,
    dismissible: true,
    duration: Infinity,
  });
};

const handleBuildMismatchSignal = (response: AxiosResponse | undefined) => {
  if (isBuildMismatchResponse(response)) {
    showBuildMismatchToast();
  }
};

export const shouldRedirectToLoginForUnauthorized = (
  status: number | undefined,
  path: string,
  requestHasAuthorization: boolean,
) => {
  if (status !== 401 || !requestHasAuthorization) {
    return false;
  }

  return path.toLowerCase() !== "/api/auth/login";
};

const resolveErrorMessage = (error: AxiosError<ErrorDetails | string>) => {
  if (!error.response) {
    return resolveTransportErrorMessage(error);
  }

  let message = error.message;

  if (
    error.response &&
    error.response.data &&
    typeof error.response.data === "object" &&
    "message" in error.response.data &&
    error.response.data.message
  ) {
    message = error.response.data.message;
  } else if (error.response && error.response.data && typeof error.response.data === "string") {
    message = error.response.data;
  }

  return message;
};

const isBrowserOnline = () => (typeof navigator === "undefined" ? undefined : navigator.onLine);

const getBackendRequestId = (error: AxiosError<ErrorDetails | string>) => {
  const data = error.response?.data;

  if (!data || typeof data !== "object" || !("requestId" in data)) {
    return undefined;
  }

  return typeof data.requestId === "string" && data.requestId.length > 0 ? data.requestId : undefined;
};

const resolveTransportKind = (error: AxiosError) => {
  if (isBrowserOnline() === false) {
    return "offline";
  }

  if (
    error.code === AxiosError.ETIMEDOUT ||
    error.code === AxiosError.ECONNABORTED ||
    error.message.toLowerCase().includes("timeout")
  ) {
    return "timeout";
  }

  if (error.code === AxiosError.ERR_NETWORK || error.message === "Network Error") {
    return "network";
  }

  return "unknown";
};

const buildRequestContext = (error: AxiosError): ApiErrorRequestContext => {
  const method = (error.config?.method ?? "unknown").toUpperCase();

  return {
    method,
    online: isBrowserOnline(),
    path: sanitizeApiRequestPath(error.config?.url, error.config?.baseURL),
    transport: error.response ? undefined : resolveTransportKind(error),
  };
};

const buildSupportBody = (message: string, requestContext: ApiErrorRequestContext) =>
  [message, `API request: ${requestContext.method} ${requestContext.path}`].filter(Boolean).join("\n");

const resolveTransportErrorMessage = (error: AxiosError) => {
  const requestContext = buildRequestContext(error);
  const endpoint = `${requestContext.method} ${requestContext.path}`;

  switch (requestContext.transport) {
    case "offline":
      return `API transport error: browser offline while calling ${endpoint}`;
    case "timeout":
      return `API transport error: timeout while calling ${endpoint}`;
    case "network":
      return `API transport error: network failure while calling ${endpoint}`;
    default:
      return `API transport error: request failed before receiving a response from ${endpoint}`;
  }
};

// Add a request interceptor that always gets the token from localStorage
AXIOS_INSTANCE.interceptors.request.use((config) => {
  // Get the most up-to-date token directly from localStorage
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);

  setRequestHeader(config.headers, FRONTEND_BUILD_HEADER_NAME, FRONTEND_BUILD_ID);

  if (token) {
    setRequestHeader(config.headers, "Authorization", `Bearer ${token}`);
  }

  return config;
});

AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    handleBuildMismatchSignal(response);
    return response;
  },
  (error: AxiosError<ErrorDetails | string>) => {
    if (error instanceof CanceledError) {
      return Promise.reject(error);
    }

    if (!Axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    handleBuildMismatchSignal(error.response);

    const requestContext = {
      ...buildRequestContext(error),
      requestId: getBackendRequestId(error),
    };
    const message = resolveErrorMessage(error);

    if (
      shouldRedirectToLoginForUnauthorized(status, requestContext.path, hasAuthorizationHeader(error.config?.headers))
    ) {
      handleUnauthorizedResponse?.();

      const enhancedError = markApiError(new Error("Unauthorized"), status, requestContext);
      enhancedError.name = "API Error 401";
      enhancedError.cause = error;

      return Promise.reject(enhancedError);
    }

    if (status === 404 && requestContext.path === "/api/tenant-info") {
      return Promise.reject(error);
    }

    const supportBody = buildSupportBody(message, requestContext);

    toast.error(message, {
      description:
        status && status >= 500
          ? "This was logged automatically. You can add details about what you were doing or email support."
          : "Need help with this? You can send a support email.",
      action:
        status && status >= 500
          ? {
              label: "Report bug",
              onClick: () => {
                void openBugReport({
                  backendRequestId: requestContext.requestId,
                  body: `I saw this error in Veronics:\n\n${supportBody}\n\nI was trying to:`,
                  subject: "Veronics bug report",
                });
              },
            }
          : undefined,
      cancel: {
        label: "Email support",
        onClick: () => {
          openSupportEmail({
            backendRequestId: requestContext.requestId,
            body: `I need help with this Veronics error:\n\n${supportBody}\n\nI was trying to:`,
          });
        },
      },
      duration: 12000,
    });

    // Enhance the error with the detailed message for better Sentry reporting
    const enhancedError = markApiError(new Error(message), status, requestContext);
    enhancedError.name = status
      ? `API Error ${status}`
      : `API Transport Error ${requestContext.transport || "unknown"}`;
    enhancedError.cause = error; // Preserve original error

    return Promise.reject(enhancedError);
  },
);

export const useAuthAxiosInterceptors = () => {
  const { logout } = useAuth();

  useEffect(() => {
    handleUnauthorizedResponse = () => {
      logout();
    };

    return () => {
      handleUnauthorizedResponse = null;
    };
  }, [logout]);
};

export const customInstance = <T,>(config: AxiosRequestConfig): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
    cancelToken: source.token,
  })
    // Add a miniumum delay of 500ms
    .then((response) => {
      if (import.meta.env.MODE === "development") {
        return new Promise<AxiosResponse>((resolve) => {
          const delay = Math.random() * 1000; // Generate a random delay between 0 and 1 seconds
          setTimeout(() => resolve(response), delay);
        });
      } else {
        return response;
      }
    })
    .then(({ data }) => data);

  // @ts-expect-error We are adding a cancel method to the promise
  // This is not a standard method, but we are using it to cancel the request
  promise.cancel = () => {
    source.cancel("Query was cancelled by React Query");
  };

  return promise;
};

export default customInstance;

export type ErrorType<Error> = AxiosError<Error>;
